import type { ReactElement } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider } from "./auth/AuthProvider";
import { AdminGate } from "./auth/AdminGate";
import { AppLayout } from "./components/AppLayout";
import { EntityListPage } from "./pages/EntityListPage";
import { EntityDetailPage } from "./pages/EntityDetailPage";
import { SearchPage } from "./pages/SearchPage";
import { AuditPage } from "./pages/AuditPage";
import { UserDetailPage } from "./pages/UserDetailPage";
import { WalletThreeSixty } from "./pages/WalletThreeSixty";
import { CollateralWithdrawalsPage } from "./pages/CollateralWithdrawals";
import { AppVersionsPage } from "./pages/AppVersionsPage";
import { FeatureFlagsPage } from "./pages/FeatureFlagsPage";
import { GenerationsPage } from "./pages/GenerationsPage";
import { StuckMigrationsPage } from "./pages/StuckMigrationsPage";
import { OldWalletResidualsPage } from "./pages/OldWalletResidualsPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { DashboardPage } from "./pages/DashboardPage";
import { AccountDeletionPage, AccountDeletionDetailPage } from "./pages/AccountDeletionPage";
import { ALL_CONFIGS } from "./entities/registry";
import { CardRefundQueuePage } from "./pages/CardRefundQueuePage";
import { CardRefundDetailPage } from "./pages/CardRefundDetailPage";
import { entityHasDetail, entityListPath, PATHS } from "./routes/paths";

/** Detail screens that aren't the generic renderer. */
const BESPOKE_DETAIL: Partial<Record<string, ReactElement>> = {
  user: <UserDetailPage />,
  wallet: <WalletThreeSixty />,
  cardRefund: <CardRefundDetailPage />,
};

export function App() {
  return (
    <AuthProvider>
      <AdminGate>
        <Routes>
          <Route element={<AppLayout />}>
            <Route path={PATHS.root} element={<Navigate to={PATHS.dashboard} replace />} />
            <Route path={PATHS.dashboard} element={<DashboardPage />} />
            <Route path={PATHS.search} element={<SearchPage />} />
            <Route path={PATHS.audit} element={<AuditPage />} />
            <Route path={PATHS.accountDeletions} element={<AccountDeletionPage />} />
            <Route path={`${PATHS.accountDeletions}/:id`} element={<AccountDeletionDetailPage />} />
            <Route path={PATHS.collateralWithdrawals} element={<CollateralWithdrawalsPage />} />
            <Route path={PATHS.appVersions} element={<AppVersionsPage />} />
            <Route path={PATHS.featureFlags} element={<FeatureFlagsPage />} />
            <Route path={PATHS.generations} element={<GenerationsPage />} />
            <Route path={PATHS.stuckMigrations} element={<StuckMigrationsPage />} />
            <Route path={PATHS.oldWalletResiduals} element={<OldWalletResidualsPage />} />

            {ALL_CONFIGS.map((config) => (
              <Route
                key={`list-${config.key}`}
                path={entityListPath(config.key)}
                element={
                  config.key === "cardRefund" ? (
                    <CardRefundQueuePage />
                  ) : (
                    <EntityListPage config={config} />
                  )
                }
              />
            ))}

            {ALL_CONFIGS.filter((c) => entityHasDetail(c.key)).map((config) => (
              <Route
                key={`detail-${config.key}`}
                path={`${entityListPath(config.key)}/:id`}
                element={BESPOKE_DETAIL[config.key] ?? <EntityDetailPage config={config} />}
              />
            ))}

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </AdminGate>
    </AuthProvider>
  );
}
